/**
 * useSlabState - Floor slab state management
 *
 * A slab has dual role:
 * - Floor surface for the room above
 * - Ceiling surface for the room below
 *
 * Defined by:
 * - Boundary (closed polyline from geometry)
 * - Thickness
 * - Elevation (top of slab = floor level)
 * - LayerSet for construction layers
 */

import { useState, useCallback, useMemo } from 'react';

let slabIdCounter = 0;
function generateSlabId() {
  return `slab_${Date.now()}_${++slabIdCounter}`;
}

/**
 * Calculate polygon area using the shoelace formula
 */
function calcPolygonArea(points) {
  if (points.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return Math.abs(area) / 2;
}

/**
 * Hook for managing floor slab state
 * @param {Object} geometryState - Reference to useGeometryState for curve lookups
 * @returns {Object} Slab state and operations
 */
export function useSlabState(geometryState = null) {
  const [slabs, setSlabs] = useState([]);

  // ============= CREATE =============

  /**
   * Create a floor slab from a closed geometry curve
   * @param {string} boundaryCurveId - ID of closed polyline/polygon in geometry state
   * @param {Object} options
   * @param {number} options.thickness - Slab thickness in mm (default 200)
   * @param {number} options.elevation - Top of slab elevation in mm (default 0)
   * @param {string} options.layerSetId - LayerSet ID
   * @param {string} options.roomAboveId - Space ID for room above
   * @param {string} options.roomBelowId - Space ID for room below
   * @param {string} options.name - Display name
   * @returns {Object} Created slab
   */
  const createSlab = useCallback((boundaryCurveId, options = {}) => {
    // Get boundary points from geometry
    const curves = geometryState?.curves || [];
    const curve = curves.find(c => c.id === boundaryCurveId);

    let boundaryPoints = [];
    if (curve) {
      if (curve.getDisplayPoints) {
        boundaryPoints = curve.getDisplayPoints().map(p => ({ x: p.x, y: p.y }));
      } else if (curve.points) {
        boundaryPoints = curve.points.map(p => ({ x: p.x, y: p.y }));
      }
    }

    const area = calcPolygonArea(boundaryPoints);

    const slab = {
      id: generateSlabId(),
      boundaryCurveId,
      boundaryPoints,
      thickness: options.thickness ?? 200,
      elevation: options.elevation ?? 0,     // Top of slab
      layerSetId: options.layerSetId || null,
      roomAboveId: options.roomAboveId || null,
      roomBelowId: options.roomBelowId || null,
      name: options.name || 'Geschossdecke',
      // Quantities
      area,           // mm²
      volume: area * (options.thickness ?? 200), // mm³
      // Timestamps
      createdAt: Date.now(),
      modifiedAt: Date.now(),
    };

    setSlabs(prev => [...prev, slab]);
    return slab;
  }, [geometryState?.curves]);

  // ============= UPDATE =============

  const updateSlab = useCallback((slabId, updates) => {
    setSlabs(prev => prev.map(s => {
      if (s.id !== slabId) return s;
      const updated = { ...s, ...updates, modifiedAt: Date.now() };
      // Recalculate volume if thickness changed
      if ('thickness' in updates) {
        updated.volume = updated.area * updated.thickness;
      }
      return updated;
    }));
  }, []);

  /**
   * Sync slab boundary from geometry (when curve is moved)
   */
  const syncFromGeometry = useCallback(() => {
    const curves = geometryState?.curves || [];

    setSlabs(prev => prev.map(slab => {
      const curve = curves.find(c => c.id === slab.boundaryCurveId);
      if (!curve) return slab;

      let boundaryPoints = [];
      if (curve.getDisplayPoints) {
        boundaryPoints = curve.getDisplayPoints().map(p => ({ x: p.x, y: p.y }));
      } else if (curve.points) {
        boundaryPoints = curve.points.map(p => ({ x: p.x, y: p.y }));
      }

      const area = calcPolygonArea(boundaryPoints);
      return {
        ...slab,
        boundaryPoints,
        area,
        volume: area * slab.thickness,
        modifiedAt: Date.now(),
      };
    }));
  }, [geometryState?.curves]);

  // ============= DELETE =============

  const deleteSlab = useCallback((slabId) => {
    setSlabs(prev => prev.filter(s => s.id !== slabId));
  }, []);

  // ============= QUERIES =============

  const getSlab = useCallback((id) => {
    return slabs.find(s => s.id === id) || null;
  }, [slabs]);

  /**
   * Get the slab that serves as floor for a given room
   */
  const getFloorForRoom = useCallback((roomId) => {
    return slabs.find(s => s.roomAboveId === roomId) || null;
  }, [slabs]);

  /**
   * Get the slab that serves as ceiling for a given room
   */
  const getCeilingForRoom = useCallback((roomId) => {
    return slabs.find(s => s.roomBelowId === roomId) || null;
  }, [slabs]);

  // ============= QUANTITIES =============

  const slabQuantities = useMemo(() => {
    return slabs.map(s => ({
      slabId: s.id,
      area: s.area,
      areaM2: s.area / 1e6,
      volume: s.volume,
      volumeM3: s.volume / 1e9,
      thickness: s.thickness,
    }));
  }, [slabs]);

  const totalSlabArea = useMemo(() => {
    return slabs.reduce((sum, s) => sum + s.area, 0);
  }, [slabs]);

  // ============= SERIALIZATION =============

  const toJSON = useCallback(() => ({
    slabs: slabs.map(s => ({ ...s })),
  }), [slabs]);

  const fromJSON = useCallback((json) => {
    if (json.slabs) setSlabs(json.slabs);
  }, []);

  const clear = useCallback(() => {
    setSlabs([]);
  }, []);

  return {
    slabs,
    createSlab,
    updateSlab,
    deleteSlab,
    getSlab,
    getFloorForRoom,
    getCeilingForRoom,
    syncFromGeometry,
    slabQuantities,
    totalSlabArea,
    toJSON,
    fromJSON,
    clear,
  };
}

export default useSlabState;
